import { useCallback, useRef, useState } from 'react';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { switchClasses } from '@mui/material/Switch';

import { notification } from '@webclient/components/notification';
import FormHeader from '@webclient/components/settings/components/form-header';
import { useGetUser } from '@webclient/components/settings/components/tabs/contact/hooks/useGetMe';
import LoadingSkeleton from '@webclient/components/settings/components/tabs/contact/loading-skeleton';
import useMutateMe from '@webclient/components/settings/components/tabs/general/hooks/useMutateMe';
import FieldGroup from '@webclient/components/settings/components/tabs/search/components/row';
import { StyledGradientCard } from '@webclient/components/settings/components/user-account-action-card/styles';
import { StyledScrollableTabContent } from '@webclient/components/settings/styles';
import Switch from '@webclient/components/switch';

import { queryClient } from '@webclient/providers/ReactQueryProvider';

import useQueryParams from '@webclient/hooks/useQueryParams';
import useResponsive from '@webclient/hooks/useResponsive';
import useSearchParamsState from '@webclient/hooks/useSearchParamsState';

import { UserOwnerType } from '@webclient/shared-types/agent';

import ArrowLeftIcon from '~icons/knowz-iconify/arrow-left';
import CheckVerifiedIcon from '~icons/knowz-iconify/check-verified';
import CreditSmartIcon from '~icons/knowz-iconify/credit-smart';
import CrossIcon from '~icons/knowz-iconify/cross';
import FatSlidersRoundedIcon from '~icons/knowz-iconify/fat-sliders-rounded';
import KnowledgeBaseIcon from '~icons/knowz-iconify/knowledge-base';

import { IconButton, Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';

type Props = {
  onClose: (event: React.SyntheticEvent) => void;
};

const SAVE_DEBOUNCE = 3000;

export default function Contact({ onClose }: Props) {
  const { userData, isLoadingUser } = useGetUser();
  const [, setSearchParams] = useSearchParamsState('action', '');
  const { t } = useTranslate();
  const isItDesktop = useResponsive('up', 'md');
  const [draftUserData, setDraftUserData] = useState<UserOwnerType | null>(
    null,
  );

  const { getQueryParamByKey } = useQueryParams();
  const contactTokenQueryParam = getQueryParamByKey<string | null>(
    'contact_token',
  );

  const { mutateAsync: mutateMeAsync, isPending: isMutateMePending } =
    useMutateMe({
      onError: () => {
        notification.error({
          message: t(
            'component.settings.general.updateProfile.response.error',
            'An error occurred while updating your profile',
          ),
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['me', contactTokenQueryParam],
        });
        notification.success({
          message: t(
            'component.settings.general.updateProfile.response.success',
            'Your profile has been updated',
          ),
        });
      },
    });

  const saveTimeout = useRef<NodeJS.Timeout | null>(null);

  const persistUpdate = useCallback(
    async (updatedData: UserOwnerType) => {
      if (isMutateMePending) {
        saveTimeout.current && clearTimeout(saveTimeout.current);
        saveTimeout.current = setTimeout(
          () => persistUpdate(updatedData),
          SAVE_DEBOUNCE,
        );
      } else {
        await mutateMeAsync({
          isSubscribedInApp: updatedData.isSubscribedInApp,
          isSubscribedOffers: updatedData.isSubscribedOffers,
          isSubscribedTraining: updatedData.isSubscribedTraining,
        });
      }
    },
    [isMutateMePending, mutateMeAsync],
  );

  function updateConfiguration(update: Partial<UserOwnerType>) {
    const tempData = draftUserData || userData;
    const updatedData: UserOwnerType = {
      ...tempData,
      ...update,
    };

    setDraftUserData(updatedData);
    persistUpdate(updatedData);
  }

  const isLoading = isLoadingUser || !userData;
  const data = draftUserData || userData;

  return (
    <>
      <FormHeader
        title={t(
          'layout.main.sections.header.settings.tabs.contact',
          'Contact',
        )}
      >
        {!isItDesktop && (
          <IconButton
            color="secondary"
            variant="ghost"
            onClick={onClose}
          >
            <ArrowLeftIcon />
          </IconButton>
        )}
      </FormHeader>

      <StyledScrollableTabContent
        sx={{
          px: 6,
        }}
      >
        {isLoading && <LoadingSkeleton />}
        {!isLoading && !data?.email && (
          <StyledGradientCard sx={{ mt: -1.5 }}>
            <Stack
              direction="row"
              gap={2}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <FatSlidersRoundedIcon fontSize={20} />

              <Text
                variant="textMd"
                weight="semibold"
              >
                {t(
                  'layout.main.sections.header.settings.contact.anon_card.title',
                  'Manage email preferences',
                )}
              </Text>
            </Stack>

            <Text variant="textSm">
              {t(
                'layout.main.sections.header.settings.contact.anon_card.description',
                'Even old friends like to check in once in a while. Sign Up with your email so we can stay connected.',
              )}
            </Text>

            <Stack direction="row-reverse">
              <Button
                onClick={() => setSearchParams('secure-account')}
                size="small"
                variant="outlined"
                sx={{
                  mt: 4,
                  borderColor: (theme) =>
                    theme.palette[
                      'Component colors/Components/Buttons/Secondary/button-secondary-fg'
                    ],
                  color: (theme) =>
                    theme.palette[
                      'Component colors/Components/Buttons/Secondary/button-secondary-fg'
                    ],
                }}
              >
                {t('layout.auth.buttons.singUp', 'Sign Up')}
              </Button>
            </Stack>
          </StyledGradientCard>
        )}
        {!isLoading && data?.email && (
          <>
            <StyledGradientCard sx={{ mt: -1.5 }}>
              <Stack
                direction="row"
                gap={2}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <FatSlidersRoundedIcon fontSize={20} />

                <Text
                  variant="textMd"
                  weight="semibold"
                >
                  {t(
                    'layout.main.sections.header.settings.contact.user_card.title',
                    'Manage {email}',
                    { email: data?.email },
                  )}
                </Text>
              </Stack>

              <Text variant="textSm">
                {t(
                  'layout.main.sections.header.settings.contact.user_card.description',
                  'True friends don’t grow apart even if they don’t talk often. Feel free to change your preferences or opt-out at any time.',
                )}
              </Text>
            </StyledGradientCard>

            <Stack gap={4}>
              <FieldGroup
                icon={<KnowledgeBaseIcon color="grey" />}
                title={t(
                  'layout.main.sections.header.settings.contact.training.title',
                  'Knowz training',
                )}
                description={t(
                  'layout.main.sections.header.settings.contact.training.description',
                  'New features & product tutorials.',
                )}
              >
                <Switch
                  checked={data?.isSubscribedTraining}
                  onChange={(_, isSubscribedTraining) => {
                    updateConfiguration({ isSubscribedTraining });
                  }}
                />
              </FieldGroup>

              <FieldGroup
                icon={<CreditSmartIcon color="grey" />}
                title={t(
                  'layout.main.sections.header.settings.contact.offers.title',
                  'Offers & Promotions',
                )}
                description={t(
                  'layout.main.sections.header.settings.contact.offers.description',
                  'Discounts, partnerships, and affiliation opportunities.',
                )}
              >
                <Switch
                  checked={data?.isSubscribedOffers}
                  onChange={(_, isSubscribedOffers) => {
                    updateConfiguration({ isSubscribedOffers });
                  }}
                />
              </FieldGroup>

              <FieldGroup
                icon={<CheckVerifiedIcon color="grey" />}
                title={t(
                  'layout.main.sections.header.settings.contact.notifications.title',
                  'In App Notifications',
                )}
                description={t(
                  'layout.main.sections.header.settings.contact.notifications.description',
                  'Email me when I receive a new notification in Knowz.',
                )}
              >
                <Switch
                  checked={data?.isSubscribedInApp}
                  onChange={(_, isSubscribedInApp) => {
                    updateConfiguration({ isSubscribedInApp });
                  }}
                />
              </FieldGroup>

              <FieldGroup
                icon={<CrossIcon color="grey" />}
                title={t(
                  'layout.main.sections.header.settings.contact.noContact.title',
                  'Do not send me any emails',
                )}
                description={t(
                  'layout.main.sections.header.settings.contact.noContact.description',
                  'Stop all email notifications.',
                )}
              >
                <Switch
                  sx={{
                    [`& .Mui-checked + .${switchClasses.track}`]: {
                      backgroundColor: (theme) =>
                        `${theme.palette.foreground.errorPrimary}!important`,
                    },
                  }}
                  color="info"
                  checked={
                    !data?.isSubscribedTraining &&
                    !data?.isSubscribedOffers &&
                    !data?.isSubscribedInApp
                  }
                  onChange={(_, value) => {
                    updateConfiguration({
                      isSubscribedInApp: !value,
                      isSubscribedOffers: !value,
                      isSubscribedTraining: !value,
                    });
                  }}
                />
              </FieldGroup>
            </Stack>
          </>
        )}
      </StyledScrollableTabContent>
    </>
  );
}
